// THE DROPDOWN THAT STAYS ON-BRAND WHEN IT OPENS.
//
// Every form select in the Hub used a native <select styled with the `.input`
// class — and the CLOSED control obeyed the CSS while the OPEN popup did not:
// macOS (and Windows) render a native select's option list as an OS widget in
// the system font, grey rows, blue highlight, no matter what font-family the
// CSS declares on <select> or <option>. Peter, from his own screen on
// /tickets (2026-08-26): "look at the fonts in the selection boxes." Measured
// the same day: every element on every page computes Montserrat — the one
// thing CSS could never reach was this popup. So the popup is ours now.
//
// The trigger reuses the `.input` class so it sits pixel-identical beside the
// text inputs; the list is an ARIA listbox in the page's own type. Keyboard:
// Enter/Space/ArrowDown open, arrows move, Enter picks, Escape closes, and
// typing jumps to the first matching option (the one native behavior worth
// keeping). Closes on outside pointerdown and on blur leaving the widget.
import { useEffect, useId, useMemo, useRef, useState } from 'react';

export interface BrandSelectOption {
  value: string;
  label: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  /** Strings are shorthand for { value: s, label: s }. */
  options: Array<string | BrandSelectOption>;
  /** Shown (muted) when value is '' — also rendered as a pickable blank row. */
  placeholder?: string;
  ariaLabel?: string;
  className?: string;
  disabled?: boolean;
}

export function BrandSelect({ value, onChange, options, placeholder, ariaLabel, className, disabled }: Props) {
  const opts = useMemo<BrandSelectOption[]>(
    () => options.map(o => (typeof o === 'string' ? { value: o, label: o } : o)),
    [options],
  );
  // A placeholder is a real, pickable "no choice" row — matching the native
  // <option value="">Select…</option> idiom every call site had.
  const rows = useMemo<BrandSelectOption[]>(
    () => (placeholder != null ? [{ value: '', label: placeholder }, ...opts.filter(o => o.value !== '')] : opts),
    [opts, placeholder],
  );

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listId = useId();

  const selected = rows.find(r => r.value === value);
  const label = selected?.label ?? placeholder ?? '';
  const isPlaceholder = value === '' && placeholder != null;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const idx = Math.max(0, rows.findIndex(r => r.value === value));
    setActive(idx);
    // Bring the active row into view once the list paints.
    requestAnimationFrame(() => {
      listRef.current?.querySelector<HTMLElement>(`[data-idx="${idx}"]`)?.scrollIntoView?.({ block: 'nearest' });
    });
  }, [open, rows, value]);

  const pick = (idx: number) => {
    const row = rows[idx];
    if (!row) return;
    onChange(row.value);
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'Escape') { e.preventDefault(); setOpen(false); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(rows.length - 1, a + 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(0, a - 1)); return; }
    if (e.key === 'Home') { e.preventDefault(); setActive(0); return; }
    if (e.key === 'End') { e.preventDefault(); setActive(rows.length - 1); return; }
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(active); return; }
    if (e.key.length === 1 && /\S/.test(e.key)) {
      const q = e.key.toLowerCase();
      const idx = rows.findIndex(r => r.label.toLowerCase().startsWith(q));
      if (idx >= 0) setActive(idx);
    }
  };

  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`)?.scrollIntoView?.({ block: 'nearest' });
  }, [active, open]);

  return (
    <div ref={rootRef} className={`relative ${className ?? ''}`} onKeyDown={onKeyDown}
      onBlur={(e) => { if (!rootRef.current?.contains(e.relatedTarget as Node)) setOpen(false); }}>
      <button
        type="button"
        disabled={disabled}
        className="input text-left flex items-center justify-between gap-2 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={ariaLabel}
        onClick={() => setOpen(o => !o)}
      >
        <span className={isPlaceholder ? 'text-gray-400' : 'text-brand-ink'}>{label}</span>
        {/* Drawn chevron — the Hub's icons are SVGs, never system glyphs. */}
        <svg className={`size-3.5 flex-none text-image-charcoal transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 14 14" fill="none" aria-hidden>
          <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label={ariaLabel}
          className="absolute z-30 mt-1 w-full max-h-60 overflow-auto rounded-xl border border-image-border bg-white py-1 shadow-card"
        >
          {rows.map((r, i) => {
            const isSel = r.value === value;
            const isAct = i === active;
            return (
              <li
                key={r.value === '' ? ' placeholder' : r.value}
                data-idx={i}
                role="option"
                aria-selected={isSel}
                className={`px-3.5 py-2 text-sm cursor-pointer flex items-center justify-between gap-2
                  ${r.value === '' ? 'text-gray-400' : 'text-brand-ink'}
                  ${isAct ? 'bg-image-pink/10' : ''} ${isSel ? 'font-semibold' : ''}`}
                onPointerEnter={() => setActive(i)}
                // pointerdown, not click: the trigger's blur fires between pointerdown and
                // click, and the blur handler closes the list — a click would land on nothing.
                onPointerDown={(e) => { e.preventDefault(); pick(i); }}
              >
                <span>{r.label}</span>
                {isSel && (
                  <svg className="size-3.5 flex-none text-image-pink" viewBox="0 0 14 14" fill="none" aria-hidden>
                    <path d="M2.5 7.5l3 3 6-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
